import { useMemo } from 'react';
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { useAppSelector } from 'redux/hooks';
import { integrationHealthIsoStateIds, TargetService, integrationsHash } from '@constants';
import useIntegrationHealthData from 'hooks/useIntegrationHealthData';
import { selectTeamHash } from 'selectors';
import { selectServiceMetricsTotalRaw } from 'IntegrationModule/selectors/general';
import { renderIntegrationHealthByRequestAndSuccessCountPercent } from 'utils';
import useAppNavigation from 'hooks/useAppNavigation';
import { getHealthSeverityRank } from './healthSeverity';
import { IntegrationHealth } from 'types';

// Some agent payloads include an aggregated lastSyncTime even though the FE
// type doesn't declare it — read it defensively so we display real data when
// it's present and fall back to "—" otherwise.
type IntegrationHealthWithLastSync = IntegrationHealth & {
  lastSyncTime?: string | number | Date | null;
};

type HealthRow = {
  mosaicTeamId: number;
  targetServiceId: number;
  teamName: string;
  health: IntegrationHealthWithLastSync | undefined;
  severity: number;
  lastSuccessfulSyncMs: number | null;
  errorCount: number;
};

/**
 * Health-focused replacement for SyncOverviewTable. Lists every team using the
 * selected integration, joined with its current-day health record, sorted so
 * the most-problematic integrations appear at the top. Read-only health view —
 * sync mechanics stay on the Sync Ratio tab.
 *
 * Data source: existing `fetchIntegrationServiceMetricsTotal` for the team list
 * + existing `fetchIntegrationHealth` (already dispatched by the parent page)
 * routed through galaxy-server → IS `/forward/*` → agent.
 */
const OverviewHealthTable = ({
  targetService,
  isLoading,
}: {
  targetService: TargetService;
  isLoading: boolean;
}) => {
  const { navigateToIntegrationDetailsPage, navigateToTeamProfile } = useAppNavigation();
  const teamHash = useAppSelector(selectTeamHash);
  const { data: serviceMetricsTotal } = useAppSelector(selectServiceMetricsTotalRaw);
  const { data: healthByService } = useIntegrationHealthData({
    isoStateId: integrationHealthIsoStateIds.TotalCountTable,
  });

  const integrationLabel = integrationsHash[targetService]?.label ?? targetService;

  const rows: HealthRow[] = useMemo(() => {
    // The TotalCountTable iso state writes `groupBy(payload, "serviceName")` —
    // so `healthByService[targetService]` is a *flat array* of IntegrationHealth
    // rows (one per [targetServiceId, date] from the agent), NOT an object
    // keyed by targetServiceId. Group it into per-targetServiceId latest rows
    // here.
    const rawHealthRows = (healthByService?.[targetService] ??
      []) as IntegrationHealthWithLastSync[];
    const latestHealthByTargetServiceId: Record<number, IntegrationHealthWithLastSync> = {};
    for (const row of rawHealthRows) {
      if (!row || typeof row.targetServiceId !== 'number') continue;
      const existing = latestHealthByTargetServiceId[row.targetServiceId];
      if (!existing || new Date(row.date) > new Date(existing.date)) {
        latestHealthByTargetServiceId[row.targetServiceId] = row;
      }
    }

    const uniqueByPair: Record<string, HealthRow> = {};
    for (const entry of serviceMetricsTotal) {
      const key = `${entry.mosaicTeamId}-${entry.targetServiceId}`;
      if (uniqueByPair[key]) continue;
      const team = teamHash[+entry.mosaicTeamId];
      const health = latestHealthByTargetServiceId[entry.targetServiceId];
      const errorCount = health?.errors
        ? Object.values(health.errors).reduce<number>(
            (sum, count) => sum + (typeof count === 'number' ? count : 0),
            0,
          )
        : 0;
      uniqueByPair[key] = {
        mosaicTeamId: +entry.mosaicTeamId,
        targetServiceId: +entry.targetServiceId,
        teamName: team?.name || `Team ${entry.mosaicTeamId}`,
        health,
        severity: getHealthSeverityRank(health),
        lastSuccessfulSyncMs:
          health?.lastSyncTime != null ? new Date(health.lastSyncTime).getTime() : null,
        errorCount,
      };
    }

    return Object.values(uniqueByPair).sort((a, b) => {
      if (a.severity !== b.severity) return a.severity - b.severity;
      // Within the same severity bucket, fall back to team name for stability.
      return a.teamName.localeCompare(b.teamName);
    });
  }, [serviceMetricsTotal, healthByService, teamHash, targetService]);

  const renderLastSync = (ms: number | null): string => {
    // Guard against NaN / Infinity. `new Date(NaN).toLocaleString()` returns the
    // literal string "Invalid Date" instead of throwing, so the try/catch alone
    // wouldn't catch malformed `lastSyncTime` payloads from the agent.
    if (ms == null || !Number.isFinite(ms)) return '—';
    try {
      const d = new Date(ms);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleString();
    } catch {
      return '—';
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {integrationLabel} Health — {rows.length} {rows.length === 1 ? 'team' : 'teams'}
        </Typography>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Team</TableCell>
                <TableCell>Integration ID</TableCell>
                <TableCell align="center">Health</TableCell>
                <TableCell>Last Successful Sync</TableCell>
                <TableCell align="right">Errors</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No teams configured for this integration.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const goToIntegration = navigateToIntegrationDetailsPage(
                    row.mosaicTeamId,
                    targetService,
                    row.targetServiceId,
                  );
                  const goToTeamProfile = () => navigateToTeamProfile({ teamId: row.mosaicTeamId });
                  return (
                    <TableRow
                      key={`${row.mosaicTeamId}-${row.targetServiceId}`}
                      hover
                      tabIndex={0}
                      role="button"
                      aria-label={`View ${row.teamName} ${targetService} integration details`}
                      sx={{
                        cursor: 'pointer',
                        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
                      }}
                      onClick={goToIntegration}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          goToIntegration();
                        }
                      }}
                    >
                      <TableCell
                        onClick={(e) => {
                          e.stopPropagation();
                          goToTeamProfile();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            goToTeamProfile();
                          }
                        }}
                        tabIndex={0}
                        role="link"
                        aria-label={`Open team profile for ${row.teamName}`}
                        sx={{
                          color: 'primary.main',
                          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
                        }}
                      >
                        {row.teamName}
                      </TableCell>
                      <TableCell>{row.targetServiceId}</TableCell>
                      <TableCell align="center">
                        {renderIntegrationHealthByRequestAndSuccessCountPercent({
                          integrationHealth: row.health,
                          className:
                            'icon-only inline-block remove-icon-padding remove-message-padding',
                        })}
                      </TableCell>
                      <TableCell>{renderLastSync(row.lastSuccessfulSyncMs)}</TableCell>
                      <TableCell align="right">{row.errorCount}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default OverviewHealthTable;
